import styled from '@emotion/styled';
import { NO_MEMBER_TAG_VALUE } from '@racemap/utilities/consts/starters';
import { Button, Select } from 'antd';
import type { Immutable } from 'immer';
import type { FC } from 'react';
import { IconExclamationTriangle } from '../Icons';

interface Option {
  value: string;
  label?: string;
}
type Options = Immutable<Map<string, Array<Option>>>;

interface Props {
  options: Options;
  value: Options;
  onChange: (value: Options) => void;
  showOptions?: boolean;
  resetable?: boolean;
  showAbandoned?: boolean;
  showStatus?: boolean;
}

export const FilterSelector: FC<Props> = ({
  options,
  value,
  onChange,
  showOptions = false,
  resetable = true,
  showAbandoned = false,
  showStatus = true,
}) => {
  const handleFilterChange = (selectedOptions: Array<Option>, filterKey: string) => {
    const updatedFilters = new Map(value);

    if (selectedOptions.length === 0) {
      updatedFilters.delete(filterKey);
    } else {
      updatedFilters.set(filterKey, selectedOptions);
    }

    onChange(updatedFilters);
  };

  const handleResetAll = () => {
    onChange(new Map());
  };

  const handleRemoveAbandonedFilter = (filterKey: string) => {
    handleFilterChange([], filterKey);
  };

  const abandonedTagGroups: Array<[string, Array<string>]> = [];
  for (const [tagKey, tagValue] of value) {
    if (tagKey === 'status') continue;
    if (!options.has(tagKey)) {
      abandonedTagGroups.push([tagKey, tagValue.map((e) => e.value)]);
    }
  }

  return (
    <Container>
      {resetable && (
        <DeselectButton danger disabled={value.size === 0} onClick={handleResetAll}>
          Reset Filter
        </DeselectButton>
      )}
      {Array.from(options.entries())
        .filter(
          ([tagKey]) =>
            showStatus || (tagKey.toLowerCase() !== 'state' && tagKey.toLowerCase() !== 'status'),
        )
        .map(([tagKey, tagValue]) => {
          const selectedSubTags = getOptions(value.get(tagKey));
          const optionsList = getOptions(tagValue);
          const placeholder = optionsList.map((e) => e.label || e.value).join(' | ');

          return (
            <FormItem key={tagKey}>
              <FormLabel>{tagKey}</FormLabel>
              <FormControl>
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  labelInValue
                  options={optionsList}
                  value={selectedSubTags}
                  placeholder={showOptions ? placeholder : 'Select...'}
                  allowClear
                  onChange={(options) => handleFilterChange(options, tagKey)}
                />
              </FormControl>
            </FormItem>
          );
        })}

      {showAbandoned &&
        abandonedTagGroups.map(([tagKey]) => {
          return (
            <FormItem key={tagKey}>
              <FormLabel>{tagKey}</FormLabel>
              <FormControl>
                <Button
                  type="link"
                  icon={<IconExclamationTriangle />}
                  onClick={() => handleRemoveAbandonedFilter(tagKey)}
                >
                  Abandoned, delete?
                </Button>
              </FormControl>
            </FormItem>
          );
        })}
    </Container>
  );
};

function getOptions(values?: Immutable<Array<Option>>): Array<Option> {
  if (values == null) return [];
  // if label is undefined, use value instead
  return (
    values
      .map((e) => ({ label: e.value === NO_MEMBER_TAG_VALUE ? '(none)' : e.value, ...e }))
      // filter values with a defined count greater zero
      .sort((a, b) => {
        if (a.value === NO_MEMBER_TAG_VALUE) return 1;
        if (b.value === NO_MEMBER_TAG_VALUE) return -1;
        return a.label.localeCompare(b.label);
      })
  );
}
const DeselectButton = styled(Button)`
  width: 100%;
  height: 22px;
  font-size: smaller;
  margin: 0 auto;
  display: block;
  margin-bottom: 28px;
`;

const Container = styled.div`
  width: 100%;
  display: flex;
  padding-right: 20px;
  flex-direction: column;
`;

const FormItem = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 28px;
  align-items: center;
  min-height: 32px;
`;

const FormLabel = styled.div`
  width: 25%;
  padding-right: 12px;
  text-align: right;
  font-weight: 600;
  text-transform: capitalize;
  
  &::after {    
    margin-left: 2px;
  }
`;

const FormControl = styled.div`
  flex: 1;
  max-width: 75%;
`;
